import React, { useState, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { toast } from "sonner";
import {
  Flex,
  Text,
  Button,
  Card,
  Badge,
  Spinner,
  TextField,
} from "@radix-ui/themes";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Dialog } from "@/components/shared/dialog";
import {
  ArrowLeftIcon,
  Link2Icon,
  TrashIcon,
  PlayIcon,
} from "@radix-ui/react-icons";

import { useEnrichmentApi } from "@/v2/entities/enrichment/Enrichment.api";
import { useGetSkills } from "@/v2/entities/skill/useGetSkills";
import { useEnrichmentSkillConnectionApi } from "@/v2/entities/enrichment-skill/EnrichmentSkillConnection.api";
import { ParameterMappingInterface } from "@/v2/features/enrichment-skill/ParameterMappingInterface";
import { CreateEnrichmentSkillConnectionRequest } from "@/v2/entities/enrichment-skill/EnrichmentSkillConnection.model";
import { isBrowserSkill } from "@/v2/entities/skill/Skill.model";
import { JSONSchema7 } from "json-schema";

export default function SkillConnectionPage() {
  const { enrichmentId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const enrichmentApi = useEnrichmentApi();
  const skillConnectionApi = useEnrichmentSkillConnectionApi();

  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedSkillId, setSelectedSkillId] = useState("");
  const [showTestDialog, setShowTestDialog] = useState(false);
  const [testInputs, setTestInputs] = useState({
    ticketId: "",
    conversationId: "",
    enrichmentOutput: {} as Record<string, any>,
    enrichmentOptions: {} as Record<string, any>,
  });

  const { data: enrichment, isLoading: enrichmentLoading } = useQuery({
    queryKey: ["enrichment", enrichmentId],
    queryFn: () => enrichmentApi.getById(enrichmentId || ""),
    enabled: !!enrichmentId,
  });

  const { data: skills, isLoading: skillsLoading } = useGetSkills();

  const { data: connection, isLoading: connectionLoading } = useQuery({
    queryKey: ["enrichment-skill-connection", enrichmentId],
    queryFn: () =>
      skillConnectionApi.getConnectionByEnrichment(enrichmentId || ""),
    enabled: !!enrichmentId,
  });

  const { data: mappingContext, isLoading: mappingContextLoading } = useQuery({
    queryKey: [
      "enrichment-mapping-context",
      enrichmentId,
      connection?.id, // Use connection ID instead of !!connection to avoid object reference changes
      showCreateDialog,
    ],
    queryFn: () => skillConnectionApi.getMappingContext(enrichmentId || ""),
    enabled: !!enrichmentId && (!!connection || showCreateDialog),
  });

  const createConnectionMutation = useMutation({
    mutationFn: skillConnectionApi.createConnection,
    onSuccess: () => {
      toast("Skill connection created successfully");
      setShowCreateDialog(false);
      setSelectedSkillId("");
      queryClient.invalidateQueries([
        "enrichment-skill-connection",
        enrichmentId,
      ]);
    },
    onError: (error: any) => {
      toast.error(error?.message || "Failed to create skill connection");
    },
  });

  const updateConnectionMutation = useMutation({
    mutationFn: skillConnectionApi.updateConnection,
    onSuccess: () => {
      toast("Skill connection updated successfully");
      queryClient.invalidateQueries([
        "enrichment-skill-connection",
        enrichmentId,
      ]);
    },
    onError: (error: any) => {
      toast.error(error?.message || "Failed to update skill connection");
    },
  });

  const deleteConnectionMutation = useMutation({
    mutationFn: skillConnectionApi.deleteConnection,
    onSuccess: () => {
      toast("Skill connection deleted successfully");
      queryClient.invalidateQueries([
        "enrichment-skill-connection",
        enrichmentId,
      ]);
    },
    onError: (error: any) => {
      toast.error(error?.message || "Failed to delete skill connection");
    },
  });

  const toggleConnectionMutation = useMutation({
    mutationFn: skillConnectionApi.toggleConnection,
    onSuccess: () => {
      toast("Skill connection status updated");
      queryClient.invalidateQueries([
        "enrichment-skill-connection",
        enrichmentId,
      ]);
    },
    onError: (error: any) => {
      toast.error(error?.message || "Failed to update connection status");
    },
  });

  const testConnectionMutation = useMutation({
    mutationFn: ({
      connectionId,
      testData,
    }: {
      connectionId: string;
      testData?: any;
    }) => skillConnectionApi.testConnection(connectionId, testData),
    onSuccess: (result) => {
      toast("Skill connection test completed successfully");
      console.log("Test result:", result);
    },
    onError: (error: any) => {
      toast.error(error?.message || "Skill connection test failed");
    },
  });

  const handleCreateConnection = useCallback(
    async (parameterMapping: any) => {
      if (!selectedSkillId || !enrichmentId) return;

      const request: CreateEnrichmentSkillConnectionRequest = {
        enrichmentId,
        skillId: selectedSkillId,
        parameterMapping,
        isActive: true,
      };

      await createConnectionMutation.mutateAsync(request);
    },
    [selectedSkillId, enrichmentId, createConnectionMutation]
  );

  const handleUpdateConnection = useCallback(
    async (parameterMapping: any) => {
      if (!connection) return;

      await updateConnectionMutation.mutateAsync({
        id: connection.id,
        parameterMapping,
      });
    },
    [connection, updateConnectionMutation]
  );

  const handleDeleteConnection = useCallback(async () => {
    if (!connection) return;
    await deleteConnectionMutation.mutateAsync(connection.id);
  }, [connection, deleteConnectionMutation]);

  const handleToggleConnection = useCallback(async () => {
    if (!connection) return;
    await toggleConnectionMutation.mutateAsync(connection.id);
  }, [connection, toggleConnectionMutation]);

  const executeTestDirectly = useCallback(async () => {
    if (!connection) return;

    // Always show the dialog to let users provide test data
    setShowTestDialog(true);
  }, [connection]);

  const handleTestConnection = useCallback(() => {
    if (!connection) return;

    // Always show the dialog to let users provide all test data
    setShowTestDialog(true);
  }, [connection]);

  const handleExecuteTest = useCallback(async () => {
    if (!connection) return;

    // Check which IDs are actually needed based on mappings
    const usesTicketId = connection.parameterMapping.mappings.some(
      (m) => m.source === "ticket_id"
    );
    const usesConversationId = connection.parameterMapping.mappings.some(
      (m) => m.source === "conversation_id"
    );
    const usesEnrichmentOutput = connection.parameterMapping.mappings.some(
      (m) => m.source === "enrichment_output"
    );
    const usesEnrichmentOptions = connection.parameterMapping.mappings.some(
      (m) => m.source === "enrichment_options"
    );

    // Validate inputs only if they're needed
    if (usesTicketId && !testInputs.ticketId.trim()) {
      toast.error(
        "Please provide a Ticket ID - it's required by the skill mapping"
      );
      return;
    }

    if (usesConversationId && !testInputs.conversationId.trim()) {
      toast.error(
        "Please provide a Conversation ID - it's required by the skill mapping"
      );
      return;
    }

    // Check if required enrichment output fields are filled
    if (usesEnrichmentOutput && mappingContext) {
      const requiredOutputFields = connection.parameterMapping.mappings
        .filter(m => m.source === "enrichment_output")
        .map(m => m.value as string);
      
      for (const fieldKey of requiredOutputFields) {
        const outputField = mappingContext.outputFields.find(f => f.key === fieldKey);
        if (outputField && !testInputs.enrichmentOutput[fieldKey]) {
          toast.error(`Please provide value for: ${outputField.name}`);
          return;
        }
      }
    }

    // Check if required enrichment option fields are filled
    if (usesEnrichmentOptions && mappingContext) {
      const requiredOptionFields = connection.parameterMapping.mappings
        .filter(m => m.source === "enrichment_options")
        .map(m => m.value as string);
      
      for (const fieldName of requiredOptionFields) {
        const optionField = mappingContext.optionFields.find(f => f.name === fieldName);
        if (optionField && testInputs.enrichmentOptions[fieldName] === undefined) {
          toast.error(`Please provide value for: ${optionField.name}`);
          return;
        }
      }
    }

    // Create test data with user inputs
    const testData = {
      ...(testInputs.ticketId.trim() && {
        ticketId: testInputs.ticketId.trim(),
      }),
      ...(testInputs.conversationId.trim() && {
        conversationId: testInputs.conversationId.trim(),
      }),
      ...(Object.keys(testInputs.enrichmentOutput).length > 0 && { 
        enrichmentOutput: testInputs.enrichmentOutput 
      }),
      ...(Object.keys(testInputs.enrichmentOptions).length > 0 && { 
        enrichmentOptions: testInputs.enrichmentOptions 
      }),
    };

    try {
      await testConnectionMutation.mutateAsync({
        connectionId: connection.id,
        testData,
      });
      setShowTestDialog(false);
      setTestInputs({ 
        ticketId: "", 
        conversationId: "", 
        enrichmentOutput: {},
        enrichmentOptions: {}
      });
    } catch (error) {
      // Error is handled by the mutation's onError callback
    }
  }, [connection, testInputs, testConnectionMutation]);

  // Helper function to check if required test inputs are missing
  const hasRequiredInputsMissing = useCallback(() => {
    if (!connection || !mappingContext) return false;

    const usesTicketId = connection.parameterMapping.mappings.some(
      (m) => m.source === "ticket_id"
    );
    const usesConversationId = connection.parameterMapping.mappings.some(
      (m) => m.source === "conversation_id"
    );

    // Check ticket and conversation IDs
    if ((usesTicketId && !testInputs.ticketId.trim()) ||
        (usesConversationId && !testInputs.conversationId.trim())) {
      return true;
    }

    // Check required enrichment output fields
    const requiredOutputFields = connection.parameterMapping.mappings
      .filter(m => m.source === "enrichment_output")
      .map(m => m.value as string);
    
    for (const fieldKey of requiredOutputFields) {
      const outputField = mappingContext.outputFields.find(f => f.key === fieldKey);
      if (outputField && !testInputs.enrichmentOutput[fieldKey]) {
        return true;
      }
    }

    // Check required enrichment option fields
    const requiredOptionFields = connection.parameterMapping.mappings
      .filter(m => m.source === "enrichment_options")
      .map(m => m.value as string);
    
    for (const fieldName of requiredOptionFields) {
      const optionField = mappingContext.optionFields.find(f => f.name === fieldName);
      if (optionField && testInputs.enrichmentOptions[fieldName] === undefined) {
        return true;
      }
    }

    return false;
  }, [connection, mappingContext, testInputs]);

  const availableSkills =
    skills?.filter((skill) => !connection || skill.id !== connection.skillId) ||
    [];

  const selectedSkill = useMemo(
    () => availableSkills.find((skill) => skill.id === selectedSkillId),
    [availableSkills, selectedSkillId]
  );

  const selectedSkillParameters: JSONSchema7 | undefined = useMemo(() => {
    if (!selectedSkill || isBrowserSkill(selectedSkill)) {
      return undefined;
    }
    return selectedSkill.parameters as JSONSchema7 | undefined;
  }, [selectedSkill]);

  if (enrichmentLoading || skillsLoading || connectionLoading) {
    return (
      <Flex className="w-full h-full items-center justify-center">
        <Spinner />
      </Flex>
    );
  }

  if (!enrichment) {
    return (
      <Flex className="w-full h-full items-center justify-center">
        <Text>Enrichment not found</Text>
      </Flex>
    );
  }

  return (
    <Flex direction="column" gap="6" p="6">
      {/* Header */}
      <Flex direction="row" align="center" justify="between">
        <Flex direction="row" align="center" gap="3">
          <Button
            variant="ghost"
            size="2"
            onClick={() => navigate("/enrichments")}
          >
            <ArrowLeftIcon />
          </Button>
          <Flex direction="column">
            <Text size="5" weight="bold">
              Skill Connection
            </Text>
            <Text size="3" color="gray">
              {enrichment.name}
            </Text>
          </Flex>
        </Flex>
        {!connection && (
          <Button
            onClick={() => setShowCreateDialog(true)}
            disabled={availableSkills.length === 0}
          >
            <Link2Icon />
            Connect Skill
          </Button>
        )}
      </Flex>

      {/* Current Connection or Empty State */}
      {connection ? (
        <Card className="p-6">
          <Flex direction="column" gap="4">
            <Flex direction="row" align="center" justify="between">
              <Flex direction="column" gap="2">
                <Text size="4" weight="medium">
                  Connected to: {connection.skill?.name}
                </Text>
                <Text size="2" color="gray">
                  {connection.skill?.description}
                </Text>
              </Flex>
              <Flex align="center" gap="2">
                <Badge color={connection.isActive ? "green" : "gray"}>
                  {connection.isActive ? "Active" : "Inactive"}
                </Badge>
                <Button
                  variant="soft"
                  size="2"
                  onClick={handleToggleConnection}
                  disabled={toggleConnectionMutation.isLoading}
                >
                  {connection.isActive ? "Deactivate" : "Activate"}
                </Button>
                <Button
                  variant="soft"
                  size="2"
                  onClick={handleTestConnection}
                  disabled={testConnectionMutation.isLoading}
                >
                  <PlayIcon />
                  Test
                </Button>
                <Button
                  variant="soft"
                  color="red"
                  size="2"
                  onClick={handleDeleteConnection}
                  disabled={deleteConnectionMutation.isLoading}
                >
                  <TrashIcon />
                  Disconnect
                </Button>
              </Flex>
            </Flex>

            {/* Parameter Mapping Interface */}
            {mappingContext && (
              <ParameterMappingInterface
                skillParameters={connection.skill?.parameters}
                currentMapping={connection.parameterMapping}
                mappingContext={mappingContext}
                onMappingChange={handleUpdateConnection}
                isLoading={updateConnectionMutation.isLoading}
              />
            )}
          </Flex>
        </Card>
      ) : (
        <Card className="p-8">
          <Flex direction="column" align="center" gap="4">
            <Text size="4" color="gray">
              No skill connected to this enrichment
            </Text>
            <Text size="2" color="gray">
              Connect a skill to automatically execute it when this enrichment
              processes tickets
            </Text>
            {availableSkills.length === 0 ? (
              <Text size="2" color="orange">
                No skills available. Please create a skill first.
              </Text>
            ) : (
              <Button onClick={() => setShowCreateDialog(true)}>
                <Link2Icon />
                Connect First Skill
              </Button>
            )}
          </Flex>
        </Card>
      )}

      {/* Create Connection Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <Dialog.Content size="large" className="max-h-[90vh] flex flex-col">
          <Dialog.Close />

          {/* Fixed Header */}
          <div className="flex-shrink-0 pb-4 border-b border-gray-200 dark:border-gray-700">
            <Dialog.Title>Connect Skill to Enrichment</Dialog.Title>
            <Text size="2" color="gray" className="mt-2">
              Select a skill and configure parameter mapping to connect it to
              the "{enrichment.name}" enrichment
            </Text>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto py-4 min-h-0">
            <Flex direction="column" gap="6">
              {/* Skill Selection Section */}
              <Card className="p-4">
                <Flex direction="column" gap="3">
                  <Flex align="center" gap="2">
                    <Link2Icon />
                    <Text size="3" weight="medium">
                      1. Select Skill
                    </Text>
                  </Flex>

                  {availableSkills.length === 0 ? (
                    <Card className="p-4 bg-orange-50 dark:bg-orange-950/20">
                      <Flex direction="column" gap="2">
                        <Text size="2" color="orange" weight="medium">
                          No Skills Available
                        </Text>
                        <Text size="2" color="gray">
                          You need to create at least one skill before you can
                          connect it to an enrichment. Skills define the
                          automated actions that will be executed when this
                          enrichment processes tickets.
                        </Text>
                      </Flex>
                    </Card>
                  ) : (
                    <Flex direction="column" gap="2">
                      <Text size="2" color="gray">
                        Choose a skill from your available skills:
                      </Text>
                      <Select
                        value={selectedSkillId}
                        onValueChange={setSelectedSkillId}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Choose a skill to connect" />
                        </SelectTrigger>
                        <SelectContent>
                          {availableSkills.map((skill) => (
                            <SelectItem key={skill.id} value={skill.id}>
                              <Flex direction="column" align="start" gap="1">
                                <Text size="2" weight="medium">
                                  {skill.name}
                                </Text>
                                {skill.description && (
                                  <Text size="1" color="gray">
                                    {skill.description}
                                  </Text>
                                )}
                              </Flex>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Flex>
                  )}
                </Flex>
              </Card>

              {/* Parameter Mapping Section */}
              {selectedSkillId && mappingContext ? (
                <Card className="p-4">
                  <Flex direction="column" gap="3">
                    <Flex align="center" gap="2">
                      <div className="w-5 h-5 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                        <Text
                          size="1"
                          weight="bold"
                          className="text-blue-600 dark:text-blue-400"
                        >
                          2
                        </Text>
                      </div>
                      <Text size="3" weight="medium">
                        Configure Parameter Mapping
                      </Text>
                    </Flex>
                    <Text size="2" color="gray">
                      Map data from your enrichment to the skill's input
                      parameters:
                    </Text>

                    {mappingContextLoading ? (
                      <Flex align="center" justify="center" className="py-8">
                        <Spinner />
                      </Flex>
                    ) : (
                      <ParameterMappingInterface
                        skillParameters={selectedSkillParameters}
                        currentMapping={{ mappings: [] }}
                        mappingContext={mappingContext}
                        onMappingChange={handleCreateConnection}
                        isLoading={createConnectionMutation.isLoading}
                        showSaveButton
                      />
                    )}
                  </Flex>
                </Card>
              ) : selectedSkillId ? (
                <Card className="p-4 bg-blue-50 dark:bg-blue-950/20">
                  <Flex align="center" justify="center" className="py-4">
                    <Spinner />
                    <Text size="2" color="gray" className="ml-2">
                      Loading parameter mapping options...
                    </Text>
                  </Flex>
                </Card>
              ) : null}
            </Flex>
          </div>

          {/* Fixed Footer */}
          <div className="flex-shrink-0 pt-4 border-t border-gray-200 dark:border-gray-700">
            <Flex gap="3" justify="end">
              <Button
                variant="soft"
                color="gray"
                onClick={() => {
                  setShowCreateDialog(false);
                  setSelectedSkillId("");
                }}
                disabled={createConnectionMutation.isLoading}
              >
                Cancel
              </Button>
              {!selectedSkillId && (
                <Text size="1" color="gray" className="flex items-center">
                  Select a skill to continue
                </Text>
              )}
            </Flex>
          </div>
        </Dialog.Content>
      </Dialog>

      {/* Test Connection Dialog */}
      <Dialog open={showTestDialog} onOpenChange={setShowTestDialog}>
        <Dialog.Content size="large" className="max-h-[90vh] flex flex-col">
          <Dialog.Close />

          <Dialog.Title>Test Skill Connection</Dialog.Title>
          <Text size="2" color="gray" className="mt-2">
            Provide the required data to test the skill connection
          </Text>

          {connection &&
            (() => {
              const usesTicketId = connection.parameterMapping.mappings.some(
                (m) => m.source === "ticket_id"
              );
              const usesConversationId =
                connection.parameterMapping.mappings.some(
                  (m) => m.source === "conversation_id"
                );
              const usesEnrichmentOutput = connection.parameterMapping.mappings.some(
                (m) => m.source === "enrichment_output"
              );
              const usesEnrichmentOptions = connection.parameterMapping.mappings.some(
                (m) => m.source === "enrichment_options"
              );
              const requiredInputs = [];
              if (usesTicketId) requiredInputs.push("Ticket ID");
              if (usesConversationId) requiredInputs.push("Conversation ID");
              if (usesEnrichmentOutput) requiredInputs.push("Enrichment Output");
              if (usesEnrichmentOptions) requiredInputs.push("Enrichment Options");

              return (
                <div className="flex-1 overflow-y-auto py-4 min-h-0">
                  <Flex direction="column" gap="4">
                    {requiredInputs.length > 0 && (
                      <Card className="p-4 bg-blue-50 dark:bg-blue-950/20">
                        <Flex direction="column" gap="2">
                          <Text
                            size="2"
                            weight="medium"
                            className="text-blue-700 dark:text-blue-300"
                          >
                            Required for Testing
                          </Text>
                          <Text size="2" color="gray">
                            This skill connection requires{" "}
                            {requiredInputs.join(", ")} to function properly. Please
                            provide values to test with your data.
                          </Text>
                        </Flex>
                      </Card>
                    )}

                    {/* Input fields */}
                    <Flex direction="column" gap="4">
                      {usesTicketId && (
                        <Flex direction="column" gap="2">
                          <Text size="2" weight="medium">
                            Ticket ID (Required)
                          </Text>
                          <TextField.Root
                            value={testInputs.ticketId}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                              setTestInputs((prev) => ({
                                ...prev,
                                ticketId: e.target.value,
                              }))
                            }
                            placeholder="Enter a valid ticket ID (e.g., TICKET-123)"
                          />
                          <Text size="1" color="gray">
                            The ticket ID from your support system
                          </Text>
                        </Flex>
                      )}

                      {usesConversationId && (
                        <Flex direction="column" gap="2">
                          <Text size="2" weight="medium">
                            Conversation ID (Required)
                          </Text>
                          <TextField.Root
                            value={testInputs.conversationId}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                              setTestInputs((prev) => ({
                                ...prev,
                                conversationId: e.target.value,
                              }))
                            }
                            placeholder="Enter a valid conversation ID (e.g., CONV-456)"
                          />
                          <Text size="1" color="gray">
                            The conversation ID associated with the ticket
                          </Text>
                        </Flex>
                      )}

                      {/* Enrichment Output Fields */}
                      {usesEnrichmentOutput && mappingContext && (() => {
                        const requiredOutputFields = connection.parameterMapping.mappings
                          .filter(m => m.source === "enrichment_output")
                          .map(m => m.value as string);
                        
                        const fieldsToShow = mappingContext.outputFields.filter(field => 
                          requiredOutputFields.includes(field.key)
                        );

                        if (fieldsToShow.length === 0) return null;

                        return (
                          <Flex direction="column" gap="3">
                            <Text size="2" weight="medium">
                              Enrichment Output Values (Required)
                            </Text>
                            <Card className="p-3 bg-gray-50 dark:bg-gray-900">
                              <Flex direction="column" gap="3">
                                {fieldsToShow.map((field) => (
                                  <Flex key={field.key} direction="column" gap="2">
                                    <Text size="2" weight="medium">
                                      {field.name}
                                    </Text>
                                    <TextField.Root
                                      value={testInputs.enrichmentOutput[field.key] || ""}
                                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                        setTestInputs((prev) => ({
                                          ...prev,
                                          enrichmentOutput: {
                                            ...prev.enrichmentOutput,
                                            [field.key]: e.target.value,
                                          },
                                        }))
                                      }
                                      placeholder={`Enter ${field.name.toLowerCase()}`}
                                    />
                                    <Text size="1" color="gray">
                                      {field.brief} (Type: {field.type})
                                    </Text>
                                  </Flex>
                                ))}
                              </Flex>
                            </Card>
                          </Flex>
                        );
                      })()}

                      {/* Enrichment Options Fields */}
                      {usesEnrichmentOptions && mappingContext && (() => {
                        const requiredOptionFields = connection.parameterMapping.mappings
                          .filter(m => m.source === "enrichment_options")
                          .map(m => m.value as string);
                        
                        const fieldsToShow = mappingContext.optionFields.filter(field => 
                          requiredOptionFields.includes(field.name)
                        );

                        if (fieldsToShow.length === 0) return null;

                        return (
                          <Flex direction="column" gap="3">
                            <Text size="2" weight="medium">
                              Enrichment Options Values (Required)
                            </Text>
                            <Card className="p-3 bg-gray-50 dark:bg-gray-900">
                              <Flex direction="column" gap="3">
                                {fieldsToShow.map((field) => (
                                  <Flex key={field.name} direction="column" gap="2">
                                    <Text size="2" weight="medium">
                                      {field.name}
                                    </Text>
                                    {field.type === "boolean" ? (
                                      <Flex align="center" gap="2">
                                        <input
                                          type="checkbox"
                                          checked={!!testInputs.enrichmentOptions[field.name]}
                                          onChange={(e) =>
                                            setTestInputs((prev) => ({
                                              ...prev,
                                              enrichmentOptions: {
                                                ...prev.enrichmentOptions,
                                                [field.name]: e.target.checked,
                                              },
                                            }))
                                          }
                                        />
                                        <Text size="2">
                                          {testInputs.enrichmentOptions[field.name] ? "True" : "False"}
                                        </Text>
                                      </Flex>
                                    ) : field.type === "number" ? (
                                      <TextField.Root
                                        type="number"
                                        value={testInputs.enrichmentOptions[field.name] || ""}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                          setTestInputs((prev) => ({
                                            ...prev,
                                            enrichmentOptions: {
                                              ...prev.enrichmentOptions,
                                              [field.name]: Number(e.target.value) || 0,
                                            },
                                          }))
                                        }
                                        placeholder={`Enter ${field.name.toLowerCase()}`}
                                      />
                                    ) : (
                                      <TextField.Root
                                        value={testInputs.enrichmentOptions[field.name] || ""}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                          setTestInputs((prev) => ({
                                            ...prev,
                                            enrichmentOptions: {
                                              ...prev.enrichmentOptions,
                                              [field.name]: e.target.value,
                                            },
                                          }))
                                        }
                                        placeholder={`Enter ${field.name.toLowerCase()}`}
                                      />
                                    )}
                                    <Text size="1" color="gray">
                                      {field.description || `${field.name} configuration`} (Type: {field.type})
                                    </Text>
                                  </Flex>
                                ))}
                              </Flex>
                            </Card>
                          </Flex>
                        );
                      })()}

                      {/* Show warning only for missing required fields */}
                      {hasRequiredInputsMissing() && (
                        <Card className="p-3 bg-orange-50 dark:bg-orange-950/20">
                          <Text size="2" color="orange">
                            Please provide all required data to continue testing
                          </Text>
                        </Card>
                      )}
                    </Flex>
                  </Flex>
                </div>
              );
            })()}

          <div className="flex-shrink-0 pt-4 border-t border-gray-200 dark:border-gray-700">
            <Flex gap="3" justify="end">
              <Button
                variant="soft"
                color="gray"
                onClick={() => {
                  setShowTestDialog(false);
                  setTestInputs({ 
                    ticketId: "", 
                    conversationId: "", 
                    enrichmentOutput: {},
                    enrichmentOptions: {}
                  });
                }}
                disabled={testConnectionMutation.isLoading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleExecuteTest}
                disabled={
                  testConnectionMutation.isLoading || hasRequiredInputsMissing()
                }
                loading={testConnectionMutation.isLoading}
              >
                <PlayIcon />
                Run Test
              </Button>
            </Flex>
          </div>
        </Dialog.Content>
      </Dialog>
    </Flex>
  );
}
